import React, { useState, useEffect } from "react";
import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import ApiClient from "../services/fuelandmoreapi";

import type { TCurrentVessel } from "@/types";

import { config } from "../config";

interface SearchVesselProps {
  onSelectVessel: (vessel: TCurrentVessel) => void;
  onSearchFocus: () => void;
  vesselTableExpanded: boolean;
}

const SearchVessel: React.FC<SearchVesselProps> = ({
  onSelectVessel,
  onSearchFocus,
  vesselTableExpanded,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [vessels, setVessels] = useState<TCurrentVessel[]>([]);
  const [searching, setSearching] = useState(false);

  const { apiBaseUrl } = config;
  const apiUrl = apiBaseUrl;

  const handleSearch = async () => {
    if (searchTerm.length < 3) {
      setVessels([]);
      return;
    }
    setSearching(true);
    try {
      const apiClient = new ApiClient(`${apiUrl}`);
      const result = await apiClient.getVesselsByName(searchTerm);
      setVessels(result);
    } catch (error) {
      setVessels([]);
      console.error("Error fetching vessels:", error);
    } finally {
      setSearching(false);
    }
  };

  // Fire search when user types (debounced)
  useEffect(() => {
    if (searchTerm.length >= 3) {
      const delayDebounceFn = setTimeout(() => {
        handleSearch();
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    } else {
      setVessels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Fire search when user presses Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div>
      <TextField
        label="Search by IMO, or Vessel Name"
        variant="outlined"
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={onSearchFocus}
        onKeyDown={handleKeyDown}
        style={{ marginBottom: "20px" }}
      />
      {vesselTableExpanded && (
        <TableContainer
          component={Paper}
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>IMO</TableCell>
                <TableCell>Vessel Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>DWT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searching ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Searching...
                  </TableCell>
                </TableRow>
              ) : vessels.length === 0 && searchTerm.length >= 3 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No vessels found.
                  </TableCell>
                </TableRow>
              ) : (
                vessels.map((vessel) => (
                  <TableRow
                    key={vessel.imo}
                    hover
                    onClick={() => onSelectVessel(vessel)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell>{vessel.imo}</TableCell>
                    <TableCell>{vessel.name}</TableCell>
                    <TableCell>{vessel.type}</TableCell>
                    <TableCell>{vessel.dwt}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default SearchVessel;
